/**
 * Teri chat client — streams the assistant's reply from our backend.
 *
 * Uses fetch (not the axios singleton) because we need the response body
 * as a ReadableStream for Server-Sent Events; auth still rides on the
 * httpOnly cookie and the tenant header, matching lib/api.ts. Everything
 * else about Teri (conversations, attachments) goes through axios — see
 * services/assistantConversations.ts.
 *
 * Because it sidesteps the axios instance it also sidesteps that instance's
 * 401 interceptor, so the refresh-and-retry is reimplemented below — routed
 * through the SAME singleflight, which is the whole point: refresh tokens
 * rotate, so two concurrent refreshes would leave the loser presenting a
 * revoked token and log the member out mid-sentence.
 *
 * Wire contract: backend/docs/openapi-assistant.yaml. Since v2.0.0 the server
 * owns the history: we post one message plus a conversation id, not the
 * transcript.
 */
import { ENV } from '../config/env';
import { refreshAccessTokenSingleflight } from '../lib/api';
import type { AssistantMentionRef, AssistantPage, ChatMeta } from '../types/assistant';

export type { AssistantPage } from '../types/assistant';

const TENANT_KEY = 'tereiko-active-tenant-id';

export interface StreamChatInput {
  /** Null starts a new conversation; the `meta` frame names the new id. */
  conversationId: string | null;
  message: string;
  /** Ids from POST /assistant/attachments. */
  attachments?: string[];
  mentions?: AssistantMentionRef[];
  page?: AssistantPage;
}

export interface StreamChatHandlers {
  /** Always fires first: real message ids, the title, and what got dropped. */
  onMeta: (meta: ChatMeta) => void;
  onDelta: (text: string) => void;
  /** The provider failed mid-stream. A plain-language delta arrives too. */
  onError?: (code: string) => void;
}

/** `friendly` marks a server-authored message safe to show verbatim in the chat. */
export class AssistantRequestError extends Error {
  constructor(
    message: string,
    public readonly friendly: boolean
  ) {
    super(message);
    this.name = 'AssistantRequestError';
  }
}

/** One POST attempt. The tenant header is re-read per attempt so a retry
 *  after a refresh still targets the workspace the member is looking at. */
function postChat(body: string, signal?: AbortSignal): Promise<Response> {
  const tenantId = localStorage.getItem(TENANT_KEY);
  return fetch(`${ENV.api.baseURL}/assistant/chat`, {
    method: 'POST',
    credentials: 'include',
    signal,
    headers: {
      'Content-Type': 'application/json',
      ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
    },
    body,
  });
}

/** Split one SSE frame into its event name and its joined data payload. */
function parseFrame(frame: string): { event: string; data: string } {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
    } else if (line.startsWith('data: ')) {
      dataLines.push(line.slice(6));
    }
  }
  // Per the SSE spec multiple data lines join with a newline. Deltas are
  // JSON-encoded precisely so they never need that, but honouring it keeps a
  // legacy multi-line plain chunk intact instead of fusing its lines.
  return { event, data: dataLines.join('\n') };
}

export async function streamAssistantChat(
  input: StreamChatInput,
  handlers: StreamChatHandlers,
  signal?: AbortSignal
): Promise<void> {
  // Kept as a string so the retry can replay the identical body.
  const body = JSON.stringify({
    conversation_id: input.conversationId,
    message: input.message,
    attachments: input.attachments ?? [],
    mentions: input.mentions ?? [],
    ...(input.page ? { page: input.page } : {}),
  });
  let res = await postChat(body, signal);

  // Access tokens last ~15 minutes and the panel stays open far longer than
  // that. Mirror the axios interceptor: refresh once, retry once. A second
  // 401 falls through to the error handling below rather than looping.
  if (res.status === 401) {
    try {
      await refreshAccessTokenSingleflight();
    } catch {
      if (!window.location.pathname.startsWith('/auth')) {
        window.location.href = '/auth/login';
      }
      throw new AssistantRequestError(
        'Your session expired — please sign in again.',
        true
      );
    }
    res = await postChat(body, signal);
  }

  if (!res.ok || !res.body) {
    // Non-stream errors (e.g. the plan's monthly question quota) arrive as a
    // JSON envelope — surface the server's own wording when there is one.
    let serverMessage: string | undefined;
    try {
      const parsed = (await res.json()) as { message?: string };
      serverMessage = parsed?.message;
    } catch {
      /* not JSON — fall through to the generic error */
    }
    throw new AssistantRequestError(
      serverMessage ?? `Assistant request failed (${res.status})`,
      Boolean(serverMessage)
    );
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE frames are separated by a blank line.
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';
    for (const frame of frames) {
      if (!frame.trim()) continue;
      const { event, data } = parseFrame(frame);
      if (event === 'done') return;
      if (event === 'meta') {
        try {
          handlers.onMeta(JSON.parse(data) as ChatMeta);
        } catch {
          /* a malformed meta frame must not kill the answer behind it */
        }
        continue;
      }
      if (event === 'error') {
        let code = 'PROVIDER_ERROR';
        try {
          code = (JSON.parse(data) as { code?: string }).code ?? code;
        } catch {
          /* keep the default code */
        }
        handlers.onError?.(code);
        continue;
      }
      if (!data) continue;
      // Chunks arrive JSON-encoded so newlines/spaces inside them survive
      // SSE framing. Fall back to the raw text for any legacy plain chunk.
      try {
        const parsed = JSON.parse(data);
        if (typeof parsed === 'string') handlers.onDelta(parsed);
      } catch {
        handlers.onDelta(data);
      }
    }
  }
}
